import { useCallback, useEffect, useState } from 'react';
import { businessDayOf, type ShiftRequest } from '@lemontaigu/staff';
import { useShiftRequests } from '@lemontaigu/staff/react';
import { composeShift, dayKey, fmtDayLabel, fmtHours, fmtTime, toTimeInput } from './logic.js';

// « Corriger » / « J'ai oublié de pointer » (brief docs/STAFF-ACCESS-AND-SHIFT-
// REQUESTS-BRIEF.md §C). The employee proposes; only the manager writes the
// time record — a request is never a shift until accepted. The form is two
// pre-filled time fields: for a correction they start as the shift's own times,
// so fixing a late departure is ONE edit and a tap.

export interface ShiftToFix {
  entryId: number;
  clockIn: string;
  clockOut: string | null;
}

export function RequestShiftForm({
  shift,
  defaultDay,
  onSent,
  onCancel,
}: {
  /** Set to correct an existing shift; omit to add a forgotten one. */
  shift?: ShiftToFix;
  defaultDay: string;
  onSent: () => void;
  onCancel: () => void;
}) {
  const requests = useShiftRequests();
  // The day is the clock date the shift STARTED on — not the 04:00-cutoff
  // business day — because that is the date the employee remembers.
  const [day, setDay] = useState(shift ? dayKey(new Date(shift.clockIn)) : defaultDay);
  const [start, setStart] = useState(shift ? toTimeInput(shift.clockIn) : '');
  const [end, setEnd] = useState(shift?.clockOut ? toTimeInput(shift.clockOut) : '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const send = async () => {
    const composed = composeShift(day, start, end);
    if (!composed) {
      setErr('Indiquez l’heure d’arrivée et de départ');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await requests.send({
        kind: shift ? 'change' : 'add',
        targetEntryId: shift?.entryId ?? null,
        clockInIso: composed.clockInIso,
        clockOutIso: composed.clockOutIso,
        reason: reason.trim() === '' ? null : reason.trim(),
      });
    } catch (e) {
      setBusy(false);
      setErr(e instanceof Error ? e.message : String(e));
      return;
    }
    setBusy(false);
    onSent();
  };

  return (
    <div className="ts-add sr-form no-print">
      {!shift && (
        <label className="ts-field">
          <span>Jour</span>
          <input type="date" value={day} max={dayKey(new Date())} onChange={(e) => setDay(e.target.value)} />
        </label>
      )}
      <div className="sr-times">
        <label className="ts-field">
          <span>Arrivée</span>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="ts-field">
          <span>Départ</span>
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
      </div>
      <label className="ts-field">
        <span>Un mot pour la direction (optionnel)</span>
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      {err && <p className="auth-err">{err}</p>}
      <div className="cand-actions">
        <button type="button" className="auth-btn" disabled={busy} onClick={() => void send()}>
          {busy ? '…' : 'Envoyer'}
        </button>
        <button type="button" className="cand-dismiss" disabled={busy} onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );
}

const STATUS_TEXT: Record<ShiftRequest['status'], string> = {
  pending: 'En attente',
  accepted: 'Acceptée',
  modified: 'Acceptée avec modification',
  rejected: 'Refusée',
  cancelled: 'Retirée',
};

/** « Mes demandes » — pending ones can be withdrawn; decided ones show what the manager did. */
export function MyRequests({ employeeId, refreshKey }: { employeeId: string; refreshKey: number }) {
  const requests = useShiftRequests();
  const [list, setList] = useState<ShiftRequest[]>([]);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      setList(await requests.mine(employeeId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [requests, employeeId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (list.length === 0 && !err) return null;

  const withdraw = async (id: number) => {
    try {
      await requests.cancel(id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
    void load();
  };

  return (
    <section className="sr-mine no-print">
      <h3 className="section-title">Mes demandes</h3>
      {err && <p className="auth-err">{err}</p>}
      <ul className="sr-list">
        {list.map((r) => {
          // What counts is what was APPLIED: a modified request shows the
          // manager's times, not the ones the employee asked for.
          const inIso = r.finalClockIn ?? r.reqClockIn;
          const outIso = r.finalClockOut ?? r.reqClockOut;
          const hours = (new Date(outIso).getTime() - new Date(inIso).getTime()) / 3_600_000;
          return (
            <li key={r.id} className={`sr-status-${r.status}`}>
              <strong>{fmtDayLabel(businessDayOf(new Date(r.reqClockIn)))}</strong> — {fmtTime(inIso)} →{' '}
              {fmtTime(outIso)} ({fmtHours(hours)}){' '}
              <span className="sr-chip">{STATUS_TEXT[r.status]}</span>
              {r.decisionNote && <em> « {r.decisionNote} »</em>}
              {r.status === 'pending' && (
                <button type="button" className="auth-link" onClick={() => void withdraw(r.id)}>
                  Retirer
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
